import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Save,
  Share,
  Fullscreen,
  FullscreenExit,
  Refresh,
  Undo,
  Redo,
  ViewInAr,
  Help,
  FolderOpen,
  SaveAs,
  FileDownload,
  FileUpload,
  NewReleases,
  Settings
} from '@mui/icons-material';

const TopBar = ({
  onSave,
  onShare,
  onToggleFullscreen,
  isFullscreen,
  onUndo,
  onRedo,
  onRefresh,
  canUndo = false,
  canRedo = false
}) => {
  const [fileMenuAnchor, setFileMenuAnchor] = useState(null);
  const [viewMenuAnchor, setViewMenuAnchor] = useState(null);

  const handleFileMenuOpen = (event) => {
    setFileMenuAnchor(event.currentTarget);
  };

  const handleFileMenuClose = () => {
    setFileMenuAnchor(null);
  };

  const handleViewMenuOpen = (event) => {
    setViewMenuAnchor(event.currentTarget);
  };

  const handleViewMenuClose = () => {
    setViewMenuAnchor(null);
  };

  const handleNewScene = () => {
    console.log('Creating new scene...');
    handleFileMenuClose();
  };

  const handleOpenScene = () => {
    console.log('Opening scene...');
    handleFileMenuClose();
  };

  const handleSaveAs = () => {
    console.log('Save as...');
    handleFileMenuClose();
  };

  const handleExportScene = () => {
    console.log('Exporting scene...');
    handleFileMenuClose();
  };

  const handleImportScene = () => {
    console.log('Importing scene...');
    handleFileMenuClose();
  };

  return (
    <AppBar position="static" color="default" elevation={0}>
      <Toolbar sx={{ minHeight: 56, px: 2 }}>
        {/* Plugin Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
          <ViewInAr sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Storefront 3D
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

        {/* Menu Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
          <Button
            onClick={handleFileMenuOpen}
            size="small"
            sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
          >
            File
          </Button>
          
          <Button
            onClick={handleViewMenuOpen}
            size="small"
            sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
          >
            View
          </Button>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Undo">
            <span>
              <IconButton
                onClick={onUndo}
                disabled={!canUndo}
                size="small"
              >
                <Undo />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Redo">
            <span>
              <IconButton
                onClick={onRedo}
                disabled={!canRedo}
                size="small"
              >
                <Redo />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Refresh View">
            <IconButton onClick={onRefresh} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Button
            variant="outlined"
            startIcon={<Save />}
            onClick={onSave}
            size="small"
          >
            Save
          </Button>

          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={onShare}
            size="small"
          >
            Share
          </Button>
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Help">
            <IconButton size="small">
              <Help />
            </IconButton>
          </Tooltip>

          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
            <IconButton onClick={onToggleFullscreen} size="small">
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* File Menu */}
        <Menu
          anchorEl={fileMenuAnchor}
          open={Boolean(fileMenuAnchor)}
          onClose={handleFileMenuClose}
          PaperProps={{
            sx: { minWidth: 200 }
          }}
        >
          <MenuItem onClick={handleNewScene}>
            <ListItemIcon>
              <NewReleases fontSize="small" />
            </ListItemIcon>
            <ListItemText>New Scene</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={handleOpenScene}>
            <ListItemIcon>
              <FolderOpen fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open Scene</ListItemText>
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={onSave}>
            <ListItemIcon>
              <Save fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={handleSaveAs}>
            <ListItemIcon>
              <SaveAs fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save As...</ListItemText>
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={handleImportScene}>
            <ListItemIcon>
              <FileUpload fontSize="small" />
            </ListItemIcon>
            <ListItemText>Import Scene</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={handleExportScene}>
            <ListItemIcon>
              <FileDownload fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export Scene</ListItemText>
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={onShare}>
            <ListItemIcon>
              <Share fontSize="small" />
            </ListItemIcon>
            <ListItemText>Share Scene</ListItemText>
          </MenuItem>
        </Menu>

        {/* View Menu */}
        <Menu
          anchorEl={viewMenuAnchor}
          open={Boolean(viewMenuAnchor)}
          onClose={handleViewMenuClose}
          PaperProps={{
            sx: { minWidth: 180 }
          }}
        >
          <MenuItem onClick={() => { onToggleFullscreen(); handleViewMenuClose(); }}>
            <ListItemIcon>
              {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => { onRefresh(); handleViewMenuClose(); }}>
            <ListItemIcon>
              <Refresh fontSize="small" />
            </ListItemIcon>
            <ListItemText>Refresh View</ListItemText>
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={handleViewMenuClose}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Settings</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
